import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { initLogin, verifyOtp, resendOtp as resendOtpApi } from '../../api';
import { InitLoginResponse } from '../../api/login';
import { GlobalState } from '../reducers';
import { DocProfileResponse } from '../../api/fetchDoctorProfile';
import fetchMyGenieApi from '../../api/fetchMyGenie';
import fetchDoctorGenieByOID, { DocGinieProfileResponse } from '../../api/fetchDoctorGenieByOID';
import { batch } from 'react-redux';
import { getDoc, getItemFromMisc, getMe, MISC_KEYS, updateItemInMisc } from '../idbHandler';
import { GenieLastUpdatedAt } from '../../constants';
import fetchDocAccounts, { AccountResponse } from '../../api/fetchDocAccounts';
import { captureError } from '../../pages/captureError';
import { track } from '../../tracking';
import fetchMyProfile, { MyProfileResponse } from '../../api/fetchMyProfile';
import { MyGinieProfileResponse, GenieProfile } from '../../api/fetchMyGenie';
import { store } from '../../App';
import fetchMyProfiles, { Myprofiles } from '../../api/fetchMyProfiles';
import { getMyGroupAccessId, getMyProfileList } from '../localstorage';
import { GO_TO_FLUSH } from '../../routes/paths';

export const VERIFY_NUMBER = 'VERIFY_NUMBER';
export const VERIFICATION_LOADER = 'VERIFICATION_LOADER';
export const VERIFICATION_ERROR = 'VERIFICATION_ERROR';

export const VERIFY_OTP = 'VERIFY_OTP';
export const VERIFY_OTP_LOADER = 'VERIFY_OTP_LOADER';
export const VERIFY_OTP_ERROR = 'VERIFY_OTP_ERROR';

export const RESEND_OTP = 'RESEND_OTP';
export const RESET_NUMBER_ERROR = 'RESET_NUMBER_ERROR';
export const RESET_NUMBER = 'RESET_NUMBER';
export const SET_MOBILE_NUMBER = 'SET_MOBILE_NUMBER';

export const UPDATE_MY_PROFILE = 'UPDATE_MY_PROFILE';
export const UPDATE_DOCTOR_ACCOUNTS = 'UPDATE_DOCTOR_ACCOUNTS';
export const UPDATE_MY_PROFILES = 'UPDATE_MY_PROFILES';
export const UPDATE_GENIE_PROFILE = 'UPDATE_GENIE_PROFILE';

export interface VerifyNumberAction {
    type: typeof VERIFY_NUMBER;
    data: InitLoginResponse & { mobileNumber: string };
}

export interface VerificationLoaderAction {
    type: typeof VERIFICATION_LOADER;
}

export interface VerificationErrorAction {
    type: typeof VERIFICATION_ERROR;
    errorMessage: string;
}

export interface VerifyOtpLoaderAction {
    type: typeof VERIFY_OTP_LOADER;
}

export interface VerifyOtpErrorAction {
    type: typeof VERIFY_OTP_ERROR;
}

export interface VerifyOtpAction {
    type: typeof VERIFY_OTP;
    verified: boolean;
    error: string;
    docOID?: string;
    onSuccess?: () => void;
}

export interface ResendOtpAction {
    type: typeof RESEND_OTP;
    data: InitLoginResponse;
}

export interface ResetNumberErrorAction {
    type: typeof RESET_NUMBER_ERROR;
}

export interface ResetNumberAction {
    type: typeof RESET_NUMBER;
}

export interface SetMobileNumberAction {
    type: typeof SET_MOBILE_NUMBER;
    mobileNumber: string;
}

export type LoginActions =
    | VerificationLoaderAction
    | VerifyNumberAction
    | VerificationErrorAction
    | VerifyOtpLoaderAction
    | VerifyOtpErrorAction
    | VerifyOtpAction
    | ResendOtpAction
    | ResetNumberErrorAction
    | ResetNumberAction
    | SetMobileNumberAction
    | UpdateMyProfileAction
    | UpdateGenieProfilesAction
    | UpdateDoctorAccountsAction
    | UpdateMyProfileListAction;

export const prefixCountryCode = (mobile: string): string => {
    return mobile.startsWith('+91') ? mobile : `+91${mobile}`;
};

export const setLoader = (): VerificationLoaderAction => {
    return {
        type: 'VERIFICATION_LOADER',
    };
};

export const setError = (errorMessage: string): VerificationErrorAction => {
    return {
        type: 'VERIFICATION_ERROR',
        errorMessage,
    };
};

export const verifyNumberAction = (
    data: InitLoginResponse,
    mobileNumber: string,
): VerifyNumberAction => {
    return {
        type: 'VERIFY_NUMBER',
        data: { ...data, mobileNumber },
    };
};

export const sendOtp = (mobileNumber: string) => {
    return async (dispatch: ThunkDispatch<GlobalState, null, LoginActions>): Promise<void> => {
        dispatch(setLoader());
        try {
            let method: 'mobile' | 'email' = 'mobile';
            // check if mobileNumber is all digits
            if (!/^\d+$/.test(mobileNumber)) {
                method = 'email';
            }
            const data = await initLogin(
                method === 'mobile'
                    ? prefixCountryCode(mobileNumber.replace('+91', ''))
                    : mobileNumber,
                method,
            );
            dispatch(verifyNumberAction(data, mobileNumber));
        } catch (err) {
            // TODO add insightful error message instead thrown error message
            const message = err instanceof Error ? err.message : String(err);
            dispatch(setError(message));
        }
    };
};

export const resendOtpAction = (data: InitLoginResponse): ResendOtpAction => {
    return {
        type: 'RESEND_OTP',
        data,
    };
};

export const resendOtp = () => {
    return async (
        dispatch: ThunkDispatch<GlobalState, null, LoginActions>,
        getState: () => GlobalState,
    ): Promise<void> => {
        dispatch(setLoader());
        try {
            const state = getState();
            const data = await resendOtpApi(state.login.mobileNumber.txn_id || '');
            dispatch(resendOtpAction(data));
        } catch (err) {
            // TODO add insightful error message instead thrown error message
            const message = err instanceof Error ? err.message : String(err);
            dispatch(setError(message));
        }
    };
};

export const setOtpLoader = (): VerifyOtpLoaderAction => {
    return {
        type: 'VERIFY_OTP_LOADER',
    };
};

export const setOtpError = (): VerifyOtpErrorAction => {
    return {
        type: 'VERIFY_OTP_ERROR',
    };
};

export const setVerifyOtp = ({
    success,
    message,
    docOID,
}: {
    success: boolean;
    message: string;
    docOID?: string;
}): VerifyOtpAction => {
    return {
        type: 'VERIFY_OTP',
        verified: success,
        error: message,
        docOID: docOID,
    };
};

export const verifyOtpAction = (otp: string, onSuccess?: (docId?: string) => void) => {
    return async (
        dispatch: ThunkDispatch<GlobalState, null, LoginActions>,
        getState: () => GlobalState,
    ): Promise<void> => {
        dispatch(setOtpLoader());
        const state: GlobalState = getState();

        try {
            const res = await verifyOtp(otp, state.login.mobileNumber.txn_id || '');
            dispatch(
                setVerifyOtp({
                    success: res.error?.length === 0,
                    message: res.error || '',
                    docOID: res.data?.profile?.oid,
                }),
            );

            if (onSuccess) {
                onSuccess(res.data?.profile?.oid);
            }
        } catch (e) {
            dispatch(setOtpError());
        }
    };
};

export const resetNumberError = (): ResetNumberErrorAction => {
    return {
        type: 'RESET_NUMBER_ERROR',
    };
};

export const resetNumber = (): ResetNumberAction => {
    return {
        type: 'RESET_NUMBER',
    };
};

export const setMobileNumber = (mobileNumber: string): SetMobileNumberAction => {
    return {
        type: 'SET_MOBILE_NUMBER',
        mobileNumber,
    };
};

export const fetchDoctorGenie = (
    docOID: string,
    onSuccess?: () => void,
    onFailure?: () => void,
): ThunkAction<
    Promise<DocGinieProfileResponse | void>,
    GlobalState,
    null,
    UpdateGenieProfilesAction
> => {
    return async (dispatch): Promise<DocGinieProfileResponse | void> => {
        const lastUpdated = await getItemFromMisc<GenieLastUpdatedAt>(
            docOID,
            MISC_KEYS.genieLastUpdated,
        );
        const state = store.getState();
        const isTreatmentPlanEnabled = state?.session?.isTreatmentPlanEnabled;

        return fetchDoctorGenieByOID(docOID, lastUpdated.isoString, isTreatmentPlanEnabled)
            .then((value) => {
                batch(async () => {
                    if (value.data) {
                        dispatch(
                            updateGenieProfiles(
                                [{ ...value.data, id: value.data.id || docOID }],
                                onSuccess,
                            ),
                        );

                        await updateItemInMisc(docOID, MISC_KEYS.genieLastUpdated, {
                            isoString: new Date().toISOString(),
                        });
                    }
                });
            })
            .catch((e) => {
                console.error('fetchDoctorGenieByOID =>', e);
                if (onFailure) {
                    onFailure();
                }
            });
    };
};

export const fetchMyGenie = (
    givenId?: string,
    givenDocids?: string[],
    onSuccess?: () => void,
    onFailure?: () => void,
    roles?: string[],
): ThunkAction<
    Promise<MyGinieProfileResponse | void>,
    GlobalState,
    null,
    UpdateGenieProfilesAction
> => {
    return async (dispatch): Promise<MyGinieProfileResponse | void> => {
        const id = givenId || getDoc()?._id;
        if (!id) {
            if (onFailure) {
                onFailure();
            }
            return;
        }

        const lastUpdated = await getItemFromMisc<GenieLastUpdatedAt>(
            id,
            MISC_KEYS.genieLastUpdated,
        );
        const state = store.getState();
        const isTreatmentPlanEnabled = state?.session?.isTreatmentPlanEnabled;

        const docids = givenDocids || getMe()?.docs?.map((t) => t.id);

        return fetchMyGenieApi(docids, lastUpdated.isoString, isTreatmentPlanEnabled, roles)
            .then((value) => {
                if (value.data) {
                    dispatch(updateGenieProfiles(value.data, onSuccess));
                } else {
                    throw new Error(value.error || 'No data found');
                }
            })
            .catch((e) => {
                console.error('fetchMyGenieApi =>', e);
                if (onFailure) {
                    onFailure();
                }
            });
    };
};

/**
 * @deprecated All use cases are consolidated to the new switch profile flow
 */
export const fetchDoctorAccounts = (): ThunkAction<
    Promise<AccountResponse['data'] | void>,
    GlobalState,
    null,
    UpdateDoctorAccountsAction
> => {
    return async (dispatch): Promise<AccountResponse['data'] | void> => {
        return fetchDocAccounts()
            .then((res) => {
                if (res.data) {
                    dispatch(updateDoctorAccounts(res.data));
                }
            })
            .catch((e) => {
                captureError(e);
                track('FETCH_ACCOUNT_FAILED');
            });
    };
};

export const fetchMyProfilesAction = (
    isSetup: boolean,
): ThunkAction<
    Promise<Myprofiles['data'] | void>,
    GlobalState,
    null,
    UpdateMyProfileListAction
> => {
    if (isSetup || !getMyProfileList()) {
        return async (dispatch): Promise<Myprofiles['data'] | void> => {
            return fetchMyProfiles()
                .then((res) => {
                    if (res.data) {
                        dispatch(updateMyProfiles(res.data));
                    }
                })
                .catch((e) => {
                    captureError(e);
                    track('ERROR', { what: 'fetchMyProfilesAction', error: e });
                });
        };
    } else {
        return async (dispatch): Promise<Myprofiles['data'] | void> => {
            return;
        };
    }
};

export const fetchDoc = (
    docOID?: string,
    onSuccess?: () => void,
    onFailure?: () => void,
): ThunkAction<Promise<DocProfileResponse | void>, GlobalState, null, UpdateMyProfileAction> => {
    return async (dispatch): Promise<DocProfileResponse | void> => {
        return fetchMyProfile(getMyGroupAccessId())
            .then((value) => {
                batch(async () => {
                    if (!value.loginmeta?.isvalid) {
                        throw new Error('Invalid user');
                    }

                    dispatch(updateMyProfile(value));
                    dispatch(
                        fetchMyGenie(
                            // TODO: this needs to be changed to my own oid
                            value.user['doc-id'] || value.user.oid,
                            value.docs?.map((t) => t.id),
                            onSuccess,
                            onFailure,
                            value.roles,
                        ),
                    );
                });
            })
            .catch((e) => {
                console.error('fetchMyProfile =>', e);
                if (onFailure) {
                    onFailure();
                }
            });
    };
};

export interface UpdateMyProfileAction {
    type: typeof UPDATE_MY_PROFILE;
    profile: MyProfileResponse;
}

export const updateMyProfile = (profile: MyProfileResponse): UpdateMyProfileAction => {
    return { type: UPDATE_MY_PROFILE, profile };
};

export interface UpdateDoctorAccountsAction {
    type: typeof UPDATE_DOCTOR_ACCOUNTS;
    accounts: AccountResponse['data'];
}

export const updateDoctorAccounts = (
    accounts: AccountResponse['data'],
): UpdateDoctorAccountsAction => {
    return { type: UPDATE_DOCTOR_ACCOUNTS, accounts };
};

export interface UpdateMyProfileListAction {
    type: typeof UPDATE_MY_PROFILES;
    profiles: Myprofiles['data'];
}

export const updateMyProfiles = (profiles: Myprofiles['data']): UpdateMyProfileListAction => {
    return { type: UPDATE_MY_PROFILES, profiles };
};

export interface UpdateGenieProfilesAction {
    type: typeof UPDATE_GENIE_PROFILE;
    genieProfiles: GenieProfile[];
    onSuccess?: () => void;
}

export const updateGenieProfiles = (
    genieProfiles: GenieProfile[],
    onSuccess?: () => void,
): UpdateGenieProfilesAction => {
    return { type: UPDATE_GENIE_PROFILE, genieProfiles, onSuccess };
};
